import React, { useState, useEffect } from "react";
import { useGetBrandGuideItemsGrouped } from "./hooks";
import { brandGuideAPI, BRAND_GUIDE_CATEGORIES } from "./api";
import { useT, useI18n } from "../../i18n";

interface BrandGuideItemProps {
  item: {
    id: string;
    title: string;
    description: string;
    iconType: string;
    iconColor: string;
    fileKey?: string;
  };
}

const BrandGuideItemComponent: React.FC<BrandGuideItemProps> = ({ item }) => {
  const t = useT();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const getIconColorClass = (color: string) => {
    const colorMap: { [key: string]: string } = {
      blue: "text-blue-600 bg-blue-50",
      green: "text-green-600 bg-green-50",
      purple: "text-purple-600 bg-purple-50",
      orange: "text-orange-600 bg-orange-50",
      red: "text-red-600 bg-red-50",
      gray: "text-gray-600 bg-gray-50",
    };
    return colorMap[color] || "text-gray-600 bg-gray-50";
  };

  const handleDownload = async () => {
    if (item.fileKey) {
      try {
        await brandGuideAPI.downloadFile(item.id);
      } catch (error) {
        alert(t("brandGuide.errors.downloadFailed"));
      }
    } else {
      alert(t("brandGuide.errors.noFileAvailable"));
    }
  };

  // Check if the file is an image based on file key
  const isImage = (fileKey: string) => {
    if (!fileKey) return false;
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".svg",
      ".webp",
      ".bmp",
    ];
    const lowercaseKey = fileKey.toLowerCase();
    return imageExtensions.some((ext) => lowercaseKey.includes(ext));
  };

  // Fetch preview URL for images
  useEffect(() => {
    if (item.fileKey && isImage(item.fileKey)) {
      setLoading(true);
      brandGuideAPI
        .getPreviewUrl(item.id)
        .then((url) => {
          setPreviewUrl(url);
        })
        .catch((error) => {
          console.error("Failed to load preview:", error);
          setPreviewUrl(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [item.id, item.fileKey]);

  // Render preview based on file type
  const renderPreview = () => {
    // If no file key, show default icon
    if (!item.fileKey) {
      return (
        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
          <i className="fa-solid fa-file text-gray-400 text-xl"></i>
        </div>
      );
    }

    // If it's an image and we have a preview URL, show the actual image
    if (isImage(item.fileKey) && previewUrl) {
      return (
        <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center">
          <img
            src={previewUrl}
            alt={item.title}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              // Fallback to file icon if image fails to load
              const target = e.target as HTMLImageElement;
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `
                  <div class="w-16 h-16 rounded-lg bg-blue-50 flex items-center justify-center">
                    <i class="fa-solid fa-file-image text-blue-600 text-xl"></i>
                  </div>
                `;
              }
            }}
          />
        </div>
      );
    }

    // If it's an image but still loading or no URL, show loading or image icon
    if (isImage(item.fileKey)) {
      return (
        <div className="w-16 h-16 rounded-lg bg-blue-50 flex items-center justify-center">
          {loading ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          ) : (
            <i className="fa-solid fa-file-image text-blue-600 text-xl"></i>
          )}
        </div>
      );
    }

    // For non-image files, show appropriate file icon based on extension
    const extension = item.fileKey.split(".").pop()?.toLowerCase();
    let iconClass = "fa-file";
    let bgColor = "bg-gray-100";
    let textColor = "text-gray-600";

    switch (extension) {
      case "pdf":
        iconClass = "fa-file-pdf";
        bgColor = "bg-red-50";
        textColor = "text-red-600";
        break;
      case "doc":
      case "docx":
        iconClass = "fa-file-word";
        bgColor = "bg-blue-50";
        textColor = "text-blue-600";
        break;
      case "ppt":
      case "pptx":
        iconClass = "fa-file-powerpoint";
        bgColor = "bg-orange-50";
        textColor = "text-orange-600";
        break;
      case "xls":
      case "xlsx":
        iconClass = "fa-file-excel";
        bgColor = "bg-green-50";
        textColor = "text-green-600";
        break;
      case "zip":
      case "rar":
        iconClass = "fa-file-zipper";
        bgColor = "bg-purple-50";
        textColor = "text-purple-600";
        break;
      default:
        iconClass = "fa-file";
        break;
    }

    return (
      <div
        className={`w-16 h-16 rounded-lg ${bgColor} flex items-center justify-center relative`}
      >
        <i className={`fa-solid ${iconClass} ${textColor} text-xl`}></i>
        {extension && (
          <div className="absolute -bottom-1 -right-1 bg-gray-600 text-white text-xs px-1 rounded">
            {extension.toUpperCase()}
          </div>
        )}
      </div>
    );
  };

  return (
    <li className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50/50 transition-colors">
      <div className="flex items-center space-x-4">
        {renderPreview()}
        <div>
          <span className="text-base font-medium text-gray-800 block">
            {item.title}
          </span>
          <span className="text-sm text-gray-500">{item.description}</span>
        </div>
      </div>
      <button
        className={`px-4 py-2 flex items-center rounded-lg transition-colors text-sm font-medium ${
          item.fileKey
            ? "bg-[#111827] text-white hover:bg-gray-800"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
        onClick={handleDownload}
        disabled={!item.fileKey}
      >
        <i className="fa-solid fa-download mr-2"></i> {t("brandGuide.download")}
      </button>
    </li>
  );
};

const Item: React.FC = () => {
  const { t, code } = useI18n();
  const {
    data: groupedItems,
    isLoading,
    error,
  } = useGetBrandGuideItemsGrouped();

  if (isLoading) {
    return (
      <div className="flex flex-col flex-1 h-full">
        <div className="bg-white px-8 pt-4 pb-2 border-b border-gray-200 flex flex-col gap-1 sticky top-0 z-20">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("brandGuide.title")}
          </h1>
          <p className="text-sm text-gray-500">
            {t("brandGuide.subtitle")}
          </p>
        </div>
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t("brandGuide.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col flex-1 h-full">
        <div className="bg-white px-8 pt-4 pb-2 border-b border-gray-200 flex flex-col gap-1 sticky top-0 z-20">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("brandGuide.title")}
          </h1>
          <p className="text-sm text-gray-500">
            {t("brandGuide.subtitle")}
          </p>
        </div>
        <div className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <i className="fa-solid fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
            <p className="text-gray-500">
              {t("brandGuide.loadFailed")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case BRAND_GUIDE_CATEGORIES.LOGOS_BRAND_MARKS:
        return "fa-solid fa-image";
      case BRAND_GUIDE_CATEGORIES.USAGE_GUIDELINES:
        return "fa-solid fa-book";
      case BRAND_GUIDE_CATEGORIES.TEMPLATES_ASSETS:
        return "fa-solid fa-palette";
      default:
        return "fa-solid fa-file";
    }
  };

  return (
    <div className="flex flex-col flex-1 h-full">
      {/* Sticky Header */}
      <div className="bg-white px-8 pt-4 pb-2 border-b border-gray-200 flex flex-col gap-1 sticky top-0 z-20">
        <h1 className="text-2xl font-bold text-gray-900">{t("brandGuide.title")}</h1>
        <p className="text-sm text-gray-500">
          {t("brandGuide.subtitle")}
        </p>
      </div>
      <div className="flex-1 p-8">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-8 border-b border-gray-200 bg-gray-50/50">
            <p className="text-gray-700 leading-relaxed">
              {t("brandGuide.intro")}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {t("common.lastUpdated", {
                date: new Date().toLocaleDateString(code, {
                  month: "numeric",
                  year: "numeric",
                }),
              })}
            </p>
          </div>
          <div className="p-8">
            {!groupedItems || Object.keys(groupedItems).length === 0 ? (
              <div className="text-center py-12">
                <i className="fa-solid fa-folder-open text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-lg">
                  {t("brandGuide.empty.title")}
                </p>
                <p className="text-gray-400 text-sm">
                  {t("brandGuide.empty.body")}
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(groupedItems).map(([category, items]) => (
                  <div key={category} className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <i
                        className={`${getCategoryIcon(
                          category
                        )} mr-3 text-gray-600`}
                      ></i>
                      {category}
                    </h3>
                    <ul className="space-y-4">
                      {(items as any[]).map((item) => (
                        <BrandGuideItemComponent key={item.id} item={item} />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Item;
